// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import {t} from '@lingui/core/macro';

const UPDATE_AVAILABLE_KEY = 'updater-available';
const UP_TO_DATE_KEY = 'updater-up-to-date';
const UNSUPPORTED_KEY = 'updater-unsupported';

export function pushUpdateAvailableModal(version: string | null, downloadUrl: string): void {
	ModalCommands.pushWithKey(
		modal(() => (
			<ConfirmModal
				title={t`Update available`}
				description={
					version
						? t`Version ${version} is ready to download. Open the download page to grab the latest installer.`
						: t`A new version is ready to download. Open the download page to grab the latest installer.`
				}
				primaryText={t`Open Download Page`}
				secondaryText={t`Later`}
				onPrimary={() => {
					void openExternalUrl(downloadUrl);
				}}
			/>
		)),
		UPDATE_AVAILABLE_KEY,
	);
}

export function pushUpToDateModal(currentVersion: string | null): void {
	ModalCommands.pushWithKey(
		modal(() => (
			<ConfirmModal
				title={t`You're up to date`}
				description={
					currentVersion ? t`You're running the latest version (${currentVersion}).` : t`You have the latest version.`
				}
				secondaryText={t`Close`}
			/>
		)),
		UP_TO_DATE_KEY,
	);
}

export function pushUnsupportedUpdateModal(reason: 'platform' | 'unpackaged', downloadUrl: string): void {
	ModalCommands.pushWithKey(
		modal(() => (
			<ConfirmModal
				title={t`Update manually`}
				description={
					reason === 'unpackaged'
						? t`In-app updates aren't available in development builds. Open the download page to grab the latest version.`
						: t`In-app updates aren't supported on this platform. Open the download page to grab the latest version.`
				}
				primaryText={t`Open Download Page`}
				secondaryText={t`Later`}
				onPrimary={() => {
					void openExternalUrl(downloadUrl);
				}}
			/>
		)),
		UNSUPPORTED_KEY,
	);
}

export function pushUpdateReadyModal(version: string | null, onInstall: () => void | Promise<void>): void {
	ModalCommands.pushWithKey(
		modal(() => (
			<ConfirmModal
				title={t`Update ready`}
				description={
					version
						? t`Version ${version} has been downloaded. Restart now to apply.`
						: t`The update has been downloaded. Restart now to apply.`
				}
				primaryText={t`Restart Now`}
				secondaryText={t`Later`}
				onPrimary={async () => {
					await onInstall();
				}}
			/>
		)),
		'updater-ready',
	);
}
